import { TranslateService } from '@ngx-translate/core';
import { Component, OnInit, ViewChild, Output, EventEmitter } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, Validators } from '@angular/forms';
import { SelectEvent, TabStripComponent } from '@progress/kendo-angular-layout';
import { groupBy } from 'lodash';
import { DataStateChangeEvent } from '@progress/kendo-angular-grid';
import { GridViewManager } from 'src/app/core/model/grid-view-manager';
import { GroupDescriptor } from '@progress/kendo-data-query';
import { Papa } from 'ngx-papaparse';
import { CsvError } from 'src/app/core/model/error';
import { NGXLogger } from 'ngx-logger';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { ProviderService } from 'src/app/core/service/provider.service';

@Component({
  standalone: false,
  selector: 'app-provider-import',
  templateUrl: './provider-import.component.html',
  styleUrls: ['./provider-import.component.scss']
})
export class ProviderImportComponent implements OnInit
{
  @ViewChild('tabstrip') public tabstrip: TabStripComponent;

  @Output() public loadedProviders: EventEmitter<any> = new EventEmitter();

  public enableAdvanced = false;
  public view: GridViewManager;
  public dialogOpened = false;
  public providersImported = false;
  public importForm: any;
  public resultcolumns: any[] = [
    { field: 'id', titleKey: 'ID', width: 20 },
    { field: 'fullName', titleKey: 'NAME', width: 50 },
    { field: 'organization', titleKey: 'ORGANIZATION', width: 60 },
    { field: 'npi', titleKey: 'PROVIDER-NPI', width: 50 },
    { field: 'message', titleKey: 'MESSAGE', width: 80 },
    { field: 'valid', titleKey: 'IMPORT-PROVIDERS-VALID', width: 20 }
  ];
  public importResults = [];
  public csvFile: File;
  public totalErrors = 0;
  public totalSuccesses = 0;
  public textYES = '';
  public textNO = '';
  public providerResultTexts: Array<{ text: string; key: string }> = [];
  public selectedTab = 0;

  /**
   * Errors when parsing the CSV file.
   */
  public csvErrors: CsvError[] = [];

  constructor(
    private fb: UntypedFormBuilder,
    private translate: TranslateService,
    private papa: Papa,
    private logger: NGXLogger,
    private messageHandlerService: MessageHandlerService,
    private providerService: ProviderService)
  { }

  public get importFilename(): UntypedFormControl
  {
    return this.importForm.get('importFilename');
  }

  public get validateOnly(): UntypedFormControl
  {
    return this.importForm.get('validateOnly');
  }

  public get deleteProviders(): UntypedFormControl
  {
    return this.importForm.get('deleteProviders');
  }

  public ngOnInit(): void
  {
    this.view = new GridViewManager(99999);

    this.translate.get(['YES', 'NO']).subscribe({
      next: (res) =>
      {
        this.textYES = res.YES;
        this.textNO = res.NO;
      }
    });

    this.translate.get(['IMPORT-PROVIDERS-NOT-FOUND', 'IMPORT-PROVIDERS-TO-UPDATE',
      'IMPORT-PROVIDERS-TO-CREATE',
      'IMPORT-PROVIDERS-UPDATED',
      'IMPORT-PROVIDERS-CREATED',
      'INVALID-GENDER',
      'INVALID-SPECIALTY',
      'INVALID-STATE',
      'INVALID-COUNTRY',
      'IMPORT-PROVIDERS-NPI-REQUIRED',
      'REQUIRED-PROVIDER-ORGANIZATION-OR-NAME',
      'PROVIDER-NPI-ALREADY-EXISTS',
      'IMPORT-EMAIL-INVALID',
      'IMPORT-PHONE-INVALID',
      'IMPORT-CELL-INVALID',
      'IMPORT-FAX-INVALID',
      'IMPORT-PROVIDER-LANGUAGE-REQUIRED',
      'IMPORT-PROVIDER-LANGUAGE-NOT-FOUND'
    ]).subscribe({
      next: (res) =>
      {
        for (const key in res)
        {
          if ({}.hasOwnProperty.call(res, key))
          {
            const resultText: any = {};
            resultText.text = res[key];
            resultText.key = key;
            this.providerResultTexts.push(resultText);
          }
        }
      }
    });
    this.resetGridData();
  }

  /**
   * Execute method to open the export dialog.
   */
  public execute(): void
  {
    this.buildForm();
    this.resetGridData();
    this.dialogOpened = true;
  }

  public close(): void
  {
    this.dialogOpened = false;
    if (this.providersImported)
    {
      this.loadedProviders.emit();
    }
  }

  /**
   * Importing Providers.
   *
   * @param e Event
   */
  public onImport(): void
  {
    // Import
    this.importProviders();
  }

  /**
   * Process the code CSV file import.
   */
  public handleCsvInput(files: FileList): void
  {
    this.csvFile = files.item(0);

    this.importForm.importFilename = this.csvFile.name;
    this.importFilename.setValue(this.csvFile.name);
    this.importForm.markAsDirty();
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.view.setState(state);
  }

  /**
   * Group change event handler.
   *
   * @param groups List of GroupDescriptors
   */
  public groupChange(groups: GroupDescriptor[]): void
  {
    this.view.setGroup(groups);
  }

  public onTabSelect(e: SelectEvent): void
  {
    this.selectedTab = e.index;
  }

  /**
   * Create import form.
   */
  private buildForm(): void
  {
    // Initialization
    this.providersImported = false;

    // Set form
    this.importForm = this.fb.group({
      importFilename: [
        { value: null, disabled: true },
        {
          validators: [Validators.required]
        }
      ],
      validateOnly: [{ value: true, disabled: false }],
      deleteProviders: [{ value: false, disabled: true }],
    });

    this.importForm.get('validateOnly').valueChanges.subscribe({
      next: (value: any) =>
      {
        if (value)
        {
          this.importForm.get('deleteProviders').disable();
          this.importForm.get('deleteProviders').setValue(false);
        }
        else
        {
          this.importForm.get('deleteProviders').enable();
        }
        this.importForm.get('deleteProviders').updateValueAndValidity();
      }
    });
  }

  private importProviders(): void
  {
    // Reset import results
    this.resetGridData();

    this.view.startLoading();
    this.tabstrip.selectTab(1);

    // Parse csv
    this.papa.parse(this.csvFile,
      {
        header: true,           // CSV file must have a header row
        delimiter: ',',         // values must be delimited by commas
        skipEmptyLines: true,   // Skip empty lines
        complete: result =>     // parsing file is async, and here is the call back function.
        {
          // create new codes
          const providers = result.data.map(
            p =>
            ({
              id: p.id,
              firstName: p.firstName,
              lastName: p.lastName,
              middleName: p.middleName,
              gender: p.gender,
              professionalSuffix: p.professionalSuffix,
              suffix: p.suffix,
              title: p.title,
              email: p.email,
              directAddress: p.directAddress,
              organization: p.organization,
              specialty: p.specialty,
              specialtyCode: p.specialtyCode,
              npi: p.npi,
              languages: p.languages,
              telephoneNumber: p.telephoneNumber,
              telephoneNumberExtension: p.telephoneNumberExtension,
              cellPhoneNumber: p.cellPhoneNumber,
              faxNumber: p.faxNumber,
              practiceAddress: {
                streetLine: p.addressLine1,
                streetLineAdditional: p.addressLine2,
                city: p.city,
                county: p.county,
                state: p.state,
                postalCode: p.postalCode,
                country: p.country,
                latitude: p.latitude,
                longitude: p.longitude
              },
              qualityScore: p.qualityScore,
              url: p.url,
              description: p.description
            }));

          this.csvErrors = result.errors;

          if (result.errors.length === 0) // no errors in the CSV file
          {
            this.providerService.addUpdateProviders(providers, this.validateOnly.value, this.deleteProviders.value).subscribe({
              next: (data) =>
              {
                data.forEach(d =>
                {
                  d.valid = d.valid ? this.textYES : this.textNO;
                  d.message = '';
                  d.clientMessageKeys.forEach(m =>
                  {
                    const message = this.providerResultTexts.find(x => x.key === m);
                    if (message !== undefined)
                    {
                      d.message += d.message !== '' ? ` ${message.text}` : `${message.text}`;
                    }
                    else
                    {
                      d.message += d.message !== '' ? ` ${m}` : `${m}`;
                    }
                  });
                });

                // Set imported counts
                const groups = groupBy(data, 'valid');
                this.totalErrors = groups[this.textNO] !== undefined ? groups[this.textNO].length : 0;
                this.totalSuccesses = groups[this.textYES] !== undefined ? groups[this.textYES].length : 0;

                // Set gridData
                this.importResults = data;
                this.view.setData(this.importResults);
                this.view.stopLoading();

                // If providers have been loaded, this will trigger a reload from the grid list on close
                if (!this.validateOnly.value)
                {
                  this.providersImported = true;
                }

                if (data && data.length < providers.length)
                {
                  this.messageHandlerService.displayError('PROVIDERS-SAVE-ERROR');
                }
              },
              error: () =>
              {
                this.messageHandlerService.displayError('PROVIDERS-SAVE-ERROR');
                this.view.stopLoading();
              }
            });
          }
          else
          {
            this.messageHandlerService.displayError('CSV-PARSING-ERRORS');
            this.logger.error(this.csvErrors);
            this.view.stopLoading();
          }
        },
        // IO error encountered when reading the CSV file
        error: (error) =>
        {
          this.logger.error(error);
          this.view.stopLoading();
        }
      });
  }

  private resetGridData(): void
  {
    this.importResults = [];
    this.totalErrors = 0;
    this.totalSuccesses = 0;
    this.view.setData(this.importResults);
  }
}
